/**
 * The component showing the list of the team members.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam 2026
 */
import {
  compareUserInfoType,
  UserInfo,
  UserInfoType,
} from '@amplify3/duplo-back-end'
import './TeamMemberList.css'

interface TeamMemberListProps {
  id?: string
  currentUser?: UserInfo
  list: UserInfo[]
}

function completeName(user: UserInfo): string {
  return `${user.user_lastname} ${user.user_firstname}`
}

const TeamMemberList = (props: TeamMemberListProps) => {
  const classNameBase = 'team-member-list'
  const className = props.id ? `${classNameBase}-${props.id}` : classNameBase

  // Returns -1 for the current user, 0 for everyone else.
  // Used as a sort key: current user always sorts to the top.
  const currentUserScore = (u: UserInfo) =>
    u.user_email === props.currentUser?.user_email ? -1 : 0

  // Multi-key sort using ||: each criterion returns 0 (equal) to fall
  // through to the next one, or a non-zero value to stop and use that
  // result. Criteria are evaluated left to right, short-circuiting as
  // soon as a non-zero value is found.
  function sortUser(lhs: UserInfo, rhs: UserInfo) {
    return (
      // 1. Current user always on top.
      currentUserScore(lhs) - currentUserScore(rhs) ||
      // 2. Sort by role (highest priority first).
      -compareUserInfoType(lhs.user_type, rhs.user_type) ||
      // 3. Sort by name if roles are equal.
      completeName(lhs).localeCompare(completeName(rhs)) ||
      // 4. Sort by email if names are equal.
      lhs.user_email.localeCompare(rhs.user_email)
    )
  }

  return (
    <table className={className}>
      <tbody>
        {props.list?.length > 0 &&
          Object.values(props.list)
            .sort((user1: UserInfo, user2: UserInfo) => sortUser(user1, user2))
            .map((user, index) => (
              <TeamMemberRow
                key={`team-member-row-${index}`}
                user={user}
                index={index}
                isCurrentUser={
                  user.user_email === props.currentUser?.user_email
                }
              ></TeamMemberRow>
            ))}
      </tbody>
    </table>
  )
}

interface TeamMemberRowProps {
  user: UserInfo
  isCurrentUser: boolean
  index: number
}

const TeamMemberRow = (props: TeamMemberRowProps) => {
  const roleName =
    UserInfoType.User === props.user.user_type
      ? 'Admin'
      : UserInfoType.SubUser === props.user.user_type
        ? 'Member'
        : 'Unknown'

  return (
    <tr>
      <td className="user-info">
        <div className="main-info">
          <div className="name">{completeName(props.user)}</div>
          {props.isCurrentUser && (
            <>
              <div className="separator">·</div>
              <div className="you">You</div>
            </>
          )}
        </div>
        <div className="additional-info">{props.user.user_email}</div>
      </td>
      <td className="role-cell">
        <div className={`role ${roleName.toLowerCase()}`}>{roleName}</div>
      </td>
    </tr>
  )
}
export default TeamMemberList
